import React from 'react';
import { Emoji } from 'emoji-mart';

import { navigate } from '../services/navigation-service';

import { getMentionComponent } from './custom-md-elements';
import TaskMention from './task-mention';

const getParent = (parents, type) => parents.find(el => el.type === type);

const renderRules = {
    // when unknown elements are introduced, so it wont break
    unknown: (node, children, parent, styles) => {
        return (
            <React.Fragment key={node.key}>
                {node.type}
            </React.Fragment>
        );
    },

    textgroup: (node, children, parent, styles) => {
        return (
            <React.Fragment key={node.key}>
                {children}
            </React.Fragment>
        );
    },

    inline: (node, children, parent, styles) => {
        return <React.Fragment key={node.key}>{children}</React.Fragment>;
    },

    text: (node, children, parent, styles) => {
        if (node.content === '<nifty-new-line-element>') {
            return null;
        }
        return <React.Fragment key={node.key}>{node.content}</React.Fragment>;
    },
    span: (node, children, parent, styles) => {
        return <span key={node.key}>{children}</span>;
    },

    strong: (node, children, parent, styles) => {
        return (
            <strong key={node.key} data-serialize-before="**" data-serialize-after="**">
                {children}
            </strong>
        );
    },

    s: (node, children, parent, styles) => {
        return (
            <s key={node.key}>
                {children}
            </s>
        );
    },
    // a
    link: (node, children, parent, styles) => {
        if (!node.attributes.href) {
            return <React.Fragment>{children}</React.Fragment>;
        }

        const handleClick = event => {
            navigate(node.attributes.href, event);
        }

        return <a key={node.key} href={node.attributes.href} target="_blank" onClick={handleClick}>{children}</a>;
    },
    // a with a non text element nested inside
    blocklink: (node, children, parent, styles) => {
        if (!node.attributes.href) {
            return <React.Fragment>{children}</React.Fragment>;
        }

        const handleClick = event => {
            navigate(node.attributes.href, event);
        }

        return <a key={node.key} href={node.attributes.href} target="_blank" onClick={handleClick}>{children}</a>;
    },
    em: (node, children, parent, styles) => {
        return (
            <em key={node.key} data-serialize-before="_" data-serialize-after="_">
                { children }
            </em>
        );
    },

    heading1: (node, children, parent, styles) => (
        <h1 key={node.key} data-serialize-before="# " data-serialize-after="\n">{ children }</h1>
    ),

    heading2: (node, children, parent, styles) => (
        <h2 key={node.key} data-serialize-before="## " data-serialize-after="\n">{ children }</h2>
    ),

    heading3: (node, children, parent, styles) => (
        <h3 key={node.key} data-serialize-before="### " data-serialize-after="\n">{ children }</h3>
    ),

    heading4: (node, children, parent, styles) => (
        <h4 key={node.key} data-serialize-before="#### " data-serialize-after="\n">{ children }</h4>
    ),

    heading5: (node, children, parent, styles) => (
        <h5 key={node.key} data-serialize-before="##### " data-serialize-after="\n">{ children }</h5>
    ),

    heading6: (node, children, parent, styles) => (
        <h6 key={node.key} data-serialize-before="###### " data-serialize-after="\n">{ children }</h6>
    ),

    paragraph: (node, children, parent, styles) => {
        const serializeAfter = parent[0]?.type === 'list_item' ? '\n' : '\n\n';
        return <p key={node.key} data-serialize-after={serializeAfter}>{ children }</p>
    },

    hardbreak: (node, children, parent, styles) => <br key={node.key} data-serialize-after="\n" />,

    blockquote: (node, children, parent, styles) => (
        <blockquote key={node.key} data-serialize-before="> " data-serialize-after="\n">
            {children}
        </blockquote>
    ),
    code_inline: (node, children, parent, styles) => {
        return (
            <code key={node.key} data-serialize-before="`" data-serialize-after="`">
                {node.content}
            </code>
        );
    },
    code_block: (node, children, parent, styles) => {
        return (
            <pre key={node.key} data-serialize-before="```\n" data-serialize-after="```\n">
                <code>
                    {node.content}
                </code>
            </pre>
        );
    },
    fence: (node, children, parent, styles) => {
        return (
            <pre key={node.key} data-serialize-before="```\n" data-serialize-after="```\n">
                <code>
                    {node.content}
                </code>
            </pre>
        );
    },
    pre: (node, children, parent, styles) => (
        <pre key={node.key}>
            {children}
        </pre>
    ),
    // ul
    bullet_list: (node, children, parent, styles) => {
        return (
            <ul key={node.key} data-serialize-after="\n">
                {children}
            </ul>
        );
    },
    ordered_list: (node, children, parent, styles) => {
        return (
            <ol key={node.key} data-serialize-after="\n">
                {children}
            </ol>
        );
    },
    // li
    list_item: (node, children, parent, styles) => {
        if (getParent(parent, 'bullet_list')) {
            return (
                <li key={node.key}>
                    <span className="list-bullet" data-serialize-after=" ">{'\u2022'}</span>

                    <div>{children}</div>
                </li>
            );
        }

        const orderedListParent = getParent(parent, 'ordered_list');

        if (orderedListParent) {
            return (
                <li key={node.key}>
                    <span className="list-number" data-serialize-after=" ">{((orderedListParent.attributes && orderedListParent.attributes.start) || 1) + node.index}{node.markup}</span>

                    <div>{children}</div>
                </li>
            );
        }

        return (
            <li key={node.key}>
                {children}
            </li>
        );
    },
    table: (node, children, parent, styles) => (
        <table key={node.key} data-serialize-after="\n">
            {children}
        </table>
    ),
    thead: (node, children, parent, styles) => (
        <thead key={node.key}>
            {children}
        </thead>
    ),
    tbody: (node, children, parent, styles) => <tbody key={node.key}>{children}</tbody>,
    th: (node, children, parent, styles) => {
        return (
            <th key={node.key}>
                {children}
            </th>
        );
    },
    tr: (node, children, parent, styles) => {
        return (
            <tr key={node.key}>
                {children}
            </tr>
        );
    },
    td: (node, children, parent, styles) => {
        return (
            <td key={node.key}>
                {children}
            </td>
        );
    },
    hr: (node, children, parent, styles) => {
        return <hr key={node.key} />;
    },

    // br
    softbreak: (node, children, parent, styles) => <br key={node.key} data-serialize-after="\n" />,
    image: (node, children, parent, styles) => <p key={node.key} data-serialize-after="\n">Image: { node.attributes.src }</p>,

    mention: (node, children, parent, styles) => getMentionComponent(node.attributes.type, node.attributes.id, node.key),
    emoji: (node, children, parent, styles) => {
        const emoji = [`:${node.attributes.id}:`];

        if (node.attributes.skin) {
            emoji.push(`:skin-tone-${node.attributes.skin}:`);
        }

        return <span data-serialize-before={emoji.join('')} key={node.key}><Emoji emoji={emoji.join('')} size={node.attributes.emojionly ? 32 : 20} /></span>
    },
    task: (node, children, parent, styles) => <TaskMention key={node.key} id={node.attributes.niceId} />

};

export default renderRules;
